import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { listBeatCollections } from "@/lib/bkls.functions";
import { absoluteUrl } from "@/lib/site";

export const collectionsQuery = queryOptions({
  queryKey: ["bkls", "collections"],
  queryFn: () => listBeatCollections(),
});

export const Route = createFileRoute("/beats/collections/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(collectionsQuery),
  head: ({ loaderData }) => {
    const title = "Beat Collections · Buddha KA Licensing";
    const description =
      "Browse Buddha KA beat collections — curated groupings of original productions available for licensing.";
    // While no collection holds a published beat, the hub carries no catalog
    // value — keep it out of the index so it matches the sitemap.
    const hasCatalog = (loaderData ?? []).some((c) => c.beatCount > 0);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...(hasCatalog ? [] : [{ name: "robots", content: "noindex, follow" }]),
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absoluteUrl("/beats/collections") },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: absoluteUrl("/beats/collections") }],
    };
  },

  component: CollectionsIndex,
});

function CollectionsIndex() {
  const { data } = useSuspenseQuery(collectionsQuery);
  // Empty collections are listed for orientation but are not linked as
  // catalog destinations until they actually hold published beats.
  const populated = data.filter((c) => c.beatCount > 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/beats" className="text-gold hover:text-gold-luminous">
          Beats
        </Link>{" "}
        / Collections
      </nav>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl">Collections.</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">
        Curated groupings of original Buddha KA productions. Every beat inside carries the same licence
        structure and rights documentation.
      </p>

      {populated.length === 0 ? (
        <p className="mt-12 rounded-2xl border hairline bg-charcoal p-8 text-sm text-muted-foreground">
          No collection holds a published beat yet. The catalog opens as productions are released.
        </p>
      ) : (
        <ul className="mt-12 grid gap-4 sm:grid-cols-2">
          {populated.map((c) => (
            <li key={c.slug}>
              <Link
                to="/beats/collections/$slug"
                params={{ slug: c.slug }}
                className="block rounded-2xl border hairline bg-charcoal p-6 transition-colors hover:border-gold"
              >
                <h2 className="font-serif text-2xl text-ivory">{c.name}</h2>
                {c.tagline && <p className="mt-2 text-sm text-muted-foreground">{c.tagline}</p>}
                <p className="mt-4 text-xs uppercase tracking-[0.2em] text-gold/80">
                  {c.beatCount} {c.beatCount === 1 ? "beat" : "beats"}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Link to="/beats/catalog" className="mt-12 inline-flex text-sm text-gold hover:text-gold-luminous">
        Browse the full catalog →
      </Link>
    </div>
  );
}
